/**
 * Last sign-in identifier storage
 *
 * Remembers the phone number or email a visitor last used to sign in so the
 * first step of /login can pre-fill it. Customers arriving from an SMS link on
 * a device they've used before otherwise tend to register a *second* account
 * rather than sign in to the one they already have.
 *
 * Two things write this value, and they are NOT equally trustworthy:
 *  - a successful sign-in (see `pages/cognito-login.tsx`) — VERIFIED: this
 *    device proved it holds the account.
 *  - the `?p=` param the API appends to first-party links in outbound SMS
 *    (see `lib/twilio/smsRecipientLinks.ts`), captured once at app bootstrap by
 *    {@link captureSmsRecipientPhone} — UNVERIFIED: a link can be forwarded,
 *    screenshotted into a group chat, or hand-authored by anyone.
 *
 * Surfaces that only *look up* an identifier can use either. A surface that
 * would make the value someone's account key must ask for the verified one —
 * see {@link readLastIdentifier}.
 */

import {
  formatPhoneNumber,
  normalizeSmsRecipientPhone,
  SMS_RECIPIENT_PHONE_PARAM,
} from '@farmshare/utils';

const LAST_IDENTIFIER_KEY = 'farmshare.auth.lastIdentifier';

/**
 * Set alongside the value when it came from an actual sign-in. Absent for a
 * value captured from a `?p=` link.
 */
const LAST_IDENTIFIER_VERIFIED_KEY = 'farmshare.auth.lastIdentifierVerified';

/** The auth context's token key — presence means someone is signed in here. */
const TOKEN_STORAGE_KEY = 'cognito_auth';

/**
 * The remembered identifier, or '' when nothing is stored (or storage is
 * unavailable, e.g. Safari private browsing).
 * @param options - `verifiedOnly` returns '' unless the stored value came from
 * a real sign-in on this device. Pass it anywhere the identifier could become
 * the key of a NEW account (registration), where a forwarded link would
 * otherwise mint an account against a contact the visitor doesn't control.
 * Plain lookups and /login don't need it: a wrong guess there costs a one-time
 * code that never arrives, not an account.
 */
export function readLastIdentifier(options?: {
  verifiedOnly?: boolean;
}): string {
  try {
    const value =
      window.localStorage.getItem(LAST_IDENTIFIER_KEY)?.trim() ?? '';
    if (!value) {
      return '';
    }

    if (
      options?.verifiedOnly &&
      window.localStorage.getItem(LAST_IDENTIFIER_VERIFIED_KEY) !== '1'
    ) {
      return '';
    }

    return value;
  } catch {
    return '';
  }
}

/**
 * Remember the identifier used to sign in. No-ops on an empty value so a
 * cleared field never wipes a good stored value.
 *
 * Every call site is post-sign-in, so this marks the value VERIFIED. The
 * unverified `?p=` path writes through {@link captureSmsRecipientPhone}.
 * @param identifier - Email or phone number the visitor signed in with.
 */
export function saveLastIdentifier(
  identifier: string | null | undefined,
): void {
  const trimmed = identifier?.trim();
  if (!trimmed) {
    return;
  }

  try {
    window.localStorage.setItem(LAST_IDENTIFIER_KEY, trimmed);
    window.localStorage.setItem(LAST_IDENTIFIER_VERIFIED_KEY, '1');
  } catch {
    // Storage unavailable/full — remembering the identifier is best-effort.
  }
}

/** Forget the remembered identifier. */
export function clearLastIdentifier(): void {
  try {
    window.localStorage.removeItem(LAST_IDENTIFIER_KEY);
    window.localStorage.removeItem(LAST_IDENTIFIER_VERIFIED_KEY);
  } catch {
    // Best-effort; see saveLastIdentifier.
  }
}

/**
 * Capture the `?p=` param that outbound SMS links carry, store it as the
 * remembered identifier, then strip it from the URL.
 *
 * Called once at bootstrap, before the router reads `location`, so:
 *  - it works whatever page the SMS link points at, not just /login
 *  - the number never reaches the router, the `?redirect=` round-trip,
 *    analytics, or a URL the customer might share
 *
 * The value is stored formatted for display; `authCheckUser` matches phone
 * numbers on digit order, so formatting doesn't affect account lookup.
 */
export function captureSmsRecipientPhone(): void {
  try {
    const url = new URL(window.location.href);
    const raw = url.searchParams.get(SMS_RECIPIENT_PHONE_PARAM);

    // Validate the SHAPE before trusting it. `formatPhoneNumber` returns its
    // input unchanged when the digits don't look like a US number, so without
    // this any string on any first-party URL — `?p=support@example.com` — would
    // be persisted verbatim and pre-filled into the sign-in box as if the app
    // had remembered it for the visitor.
    const phone = normalizeSmsRecipientPhone(raw);

    // Only a signed-OUT visitor has anything to gain here. Writing while a
    // session is live means a shared device — a family tablet, a shop's front
    // counter, a staffer opening a customer's link out of the vendor chat —
    // silently replaces the signed-in person's remembered identifier with
    // someone else's number, which the next sign-in then presents as
    // authoritative. Strip the param either way.
    if (phone && !window.localStorage.getItem(TOKEN_STORAGE_KEY)) {
      try {
        window.localStorage.setItem(
          LAST_IDENTIFIER_KEY,
          formatPhoneNumber(phone),
        );
        // NOT verified: this arrived in a URL, which anyone can forward.
        window.localStorage.removeItem(LAST_IDENTIFIER_VERIFIED_KEY);
      } catch {
        // Storage unavailable — best-effort, same as saveLastIdentifier.
      }
    }

    // Strip it even when blank/duplicated/invalid so a stray `?p=` never
    // lingers in the address bar or a shared URL.
    if (url.searchParams.has(SMS_RECIPIENT_PHONE_PARAM)) {
      url.searchParams.delete(SMS_RECIPIENT_PHONE_PARAM);
      window.history.replaceState(window.history.state, '', url.toString());
    }
  } catch {
    // Malformed URL or no history access — nothing to capture.
  }
}
